import { PaginationState, Table } from '@tanstack/react-table'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface DataTablePaginationProps<TData> {
  table: Table<TData>
  pageSizes?: number[]
  // Singular noun for the summary line, e.g. "payment" -> "Showing 1-20 of 128 payments".
  itemLabel?: string
}

export const defaultPageSizes = [20, 50, 100]

export const defaultPageSize = defaultPageSizes[0]

export function DataTablePagination<TData>({
  table,
  pageSizes = defaultPageSizes,
  itemLabel = 'row',
}: DataTablePaginationProps<TData>) {
  const totalRows =
    table.getRowCount() || table.getFilteredRowModel().rows.length

  return (
    <div className="flex items-center justify-between">
      <div className="text-muted-foreground hidden flex-1 text-sm md:block">
        {/* TODO: Support row selection */}
        {/* {table.getFilteredSelectedRowModel().rows.length} of{' '}
        {table.getFilteredRowModel().rows.length} row(s) selected. */}
        Showing{' '}
        {table.getState().pagination.pageIndex *
          table.getState().pagination.pageSize +
          1}
        -
        {Math.min(
          (table.getState().pagination.pageIndex + 1) *
            table.getState().pagination.pageSize,
          totalRows
        )}{' '}
        of {totalRows} {itemLabel}
        {totalRows === 1 ? '' : 's'}
      </div>
      <div className="flex flex-1 items-center space-x-6 md:flex-none lg:space-x-8">
        <div className="flex flex-1 items-center space-x-2 md:flex-none">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value))
            }}
          >
            <SelectTrigger
              className="h-8 w-auto min-w-[70px]"
              aria-label="Rows per page"
            >
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizes.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-x-2">
          <Button
            variant="outline"
            size="icon"
            className="mx-0 hidden size-8 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="mx-0 size-8"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft />
          </Button>

          <div className="items-center justify-center text-sm font-medium whitespace-nowrap tabular-nums md:min-w-[60px]">
            {table.getState().pagination.pageIndex + 1}
            <span className="hidden md:inline"> of {table.getPageCount()}</span>
          </div>

          <Button
            variant="outline"
            size="icon"
            className="mx-0 size-8"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="mx-0 hidden size-8 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  )
}

DataTablePagination.DefaultState = (): PaginationState => ({
  pageIndex: 0,
  pageSize: defaultPageSize,
})
